import React, { useState } from 'react';
import { Badge, Button, Form, InputGroup, OverlayTrigger, Popover } from 'react-bootstrap';

interface SearchDropdownProps {
    allValues: { Name: string, Id: string }[];
    selectedValues: string[];
    onValueChange: (valueId: string) => void;
    hideBadge?: boolean;
    fullValuesList?: { Name: string, Id: string, LSUCID?: any }[];
    /** When true, search and add/remove are disabled; selected users shown as static badges only. */
    disabled?: boolean;
}

interface HighlightedTextProps {
    text: string;
    highlight: string;
}

const HighlightedText: React.FC<HighlightedTextProps> = ({ text, highlight }) => {
    if (!highlight) return <>{text}</>;
    
    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return (
        <>
            {parts.map((part, i) => 
                part.toLowerCase() === highlight.toLowerCase() ? 
                    <b key={i}>{part}</b> : 
                    part
            )}
        </>
    );
};

const SearchDropdown: React.FC<SearchDropdownProps> = (props) => {
    const [valueSearch, setValueSearch] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);

    const filteredLawyers = props.allValues.filter(lawyer => 
        lawyer.Name?.toLowerCase().includes(valueSearch.toLowerCase())
    );

    const handleSearchChange = (e) => {
        setValueSearch(e.target.value);
        setShowDropdown(true);
    };

    const handleRemoveValue = (valueId: string) => {
        props.onValueChange(valueId);
    };

    const handleInputClick = () => {
        setShowDropdown(true);
    };

    // Helper function to check if an ID is an LSUCID (not only numbers)
    const isLSUCID = (id: string) => {
        return !/^\d+$/.test(id);
    };

    // Helper function to get user name, checking both filtered and full lists
    const getUserName = (userId: string) => {
        // First try to find in the filtered allValues
        const userInFiltered = props.fullValuesList.find(user => user.Id === userId || user.LSUCID === userId);
        if (userInFiltered) {
            return userInFiltered.Name;
        }
        
        // If not found and we have a full list, check there (for LSUCID users)
        if (props.fullValuesList) {
            const userInFull = props.fullValuesList.find(user => 
                (user.Id?.toString() || user.LSUCID?.toString()) === userId
            );
            return userInFull?.Name || userId;
        }
        
        return userId;
    };

    if (props.disabled) {
        const hasSelections = props.selectedValues.length > 0;
        if (props.hideBadge === true) {
            return null;
        }
        return (
            <div className="d-flex flex-wrap align-items-center" style={{ marginBottom: "-7px" }}>
                {hasSelections ? (
                    props.fullValuesList ? (
                        props.selectedValues.map((userId) => {
                            const userName = getUserName(userId);
                            const isDeletedOrMissing = !userName || userName === userId;
                            return (
                                <Badge
                                    key={userId}
                                    className="tag me-2 mb-2"
                                    pill
                                    bg={isDeletedOrMissing ? "secondary" : "info"}
                                >
                                    {userName || userId}
                                </Badge>
                            );
                        })
                    ) : (
                        props.allValues
                            .filter((value) => props.selectedValues.includes(value.Id))
                            .map((value) => (
                                <Badge key={value.Id} className="tag me-2 mb-2" pill bg="info">
                                    {value.Name}
                                </Badge>
                            ))
                    )
                ) : (
                    <span className="text-muted small">None assigned</span>
                )}
            </div>
        );
    }

    const valueFilter = (
        <Popover placement="bottom">
            <Popover.Body
                style={{ 
                    maxHeight: '300px', 
                    overflowY: 'auto'
                }}>
                
                {filteredLawyers.length > 0 ? (
                    filteredLawyers.map(lawyer => (
                        <div className="p-1" key={lawyer.Id}>
                            <Form.Check 
                                type="checkbox" 
                                label={
                                    <HighlightedText 
                                        text={lawyer.Name} 
                                        highlight={valueSearch}
                                    />
                                }
                                checked={props.selectedValues.includes(lawyer.Id)}
                                onChange={() => props.onValueChange(lawyer.Id)}
                            />
                        </div>
                    ))
                ) : (
                    <div className="p-1 text-center text-muted">
                        No search results found
                    </div>
                )}
            </Popover.Body>
        </Popover>
    );

    return (
        <>
            <OverlayTrigger 
                key="openValueFilter" 
                trigger="click"
                placement="bottom"
                overlay={valueFilter}
                rootClose
                show={showDropdown}
                onToggle={(nextShow) => setShowDropdown(nextShow)}
            >
                <InputGroup style={{ cursor: 'pointer' }}>
                    <Form.Control 
                        type="text"
                        value={valueSearch}
                        onChange={handleSearchChange}
                        onClick={handleInputClick}
                        placeholder="Search users..."
                    />
                    <InputGroup.Text>
                        { 
                            valueSearch !== '' &&
                            <Button variant="link" id="clearSearchBtn" onClick={() => {
                                setValueSearch('');
                                setShowDropdown(false);
                            }}>
                                <i className="fa-regular fa-xmark"></i>
                            </Button>
                        }
                    </InputGroup.Text>
                </InputGroup>
            </OverlayTrigger>

            {props.hideBadge !== true && <div className="d-flex flex-wrap mt-2" style={{ marginBottom: "-7px" }}>
                {props.fullValuesList ? (
                    // New logic with LSUCID handling
                    props.selectedValues.map(userId => {
                        const userName = getUserName(userId);
                        const isLSUCIDUser = isLSUCID(userId);
                        const isDeletedOrMissing = !userName || userName === userId;
                        
                        return (
                            <Badge 
                                key={userId}
                                className={`tag ${!isLSUCIDUser ? 'removable' : ''} me-2 mb-2`}
                                pill
                                bg={isDeletedOrMissing ? "secondary" : "info"}
                                onClick={!isLSUCIDUser ? () => handleRemoveValue(userId) : undefined}
                                style={{ cursor: (!isLSUCIDUser && !isDeletedOrMissing) ? 'pointer' : 'default' }}
                            >
                                {userName}
                            </Badge>
                        );
                    })
                ) : (
                    // Original logic
                    props.allValues
                        .filter(value => props.selectedValues.includes(value.Id))
                        .map(value => (
                            <Badge 
                                key={value.Id}
                                className="tag removable me-2 mb-2" 
                                pill
                                bg="info" 
                                onClick={() => handleRemoveValue(value.Id)}
                            >
                                {value.Name}
                            </Badge>
                        ))
                )}
            </div>}
        </>
    );
};

export default SearchDropdown; 